/**
 * Transfers server-side auth state to the client via useState.
 * Runs during SSR only — reads event.context.auth set by server/middleware/auth.ts.
 * This prevents the login page flash for logged-in users while Firebase initializes.
 */
import { normalizeCountryCode } from '~~/shared/analytics/consent'
import { SESSION_HINT_COOKIE } from '~~/shared/auth/sessionCookie'

export default defineNuxtPlugin(() => {
  if (import.meta.client) return

  const event = useRequestEvent()
  const rawCountryHeader = event?.node?.req?.headers['cf-ipcountry']
  const headerValue = Array.isArray(rawCountryHeader) ? rawCountryHeader[0] : rawCountryHeader
  const visitorCountry = normalizeCountryCode(headerValue)
  useState<string | null>('visitor-country', () => visitorCountry)

  const auth = event?.context?.auth

  // When the verified __session cookie is present at SSR time, seed the auth gate
  // as resolved (`auth-loading = false`) so SSR paints the authenticated state
  // immediately. When no cookie is present (`!auth`), seed `auth-loading = true`
  // so marketing and landing CTAs render a loading spinner until the client-side
  // Firebase SDK finishes checking IndexedDB.
  useState('auth-loading', () => !auth)
  /**
   * MAR-1421: no verified session, but this browser has held one (`__session_hint` outlives the
   * one-hour `__session`). On an /app route, render the app's loading shell and let the client
   * restore the persisted Firebase session in place: `auth.global.ts` skips its SSR redirect to
   * /login, and `default.vue` keeps the shell up until it has mounted so the hydrated render matches
   * this one. The client then decides, sending the user to /login only if there really is no session.
   */
  const pathname = (event?.path ?? '').split('?')[0] ?? ''
  const isAppPath = pathname === '/app' || pathname.startsWith('/app/')
  const hasSessionHint = Boolean(useCookie(SESSION_HINT_COOKIE).value)
  useState('auth-deferred', () => !auth && isAppPath && hasSessionHint)

  useState('auth-user', () => auth
    ? {
        uid: auth.uid,
        email: auth.email,
        displayName: auth.displayName,
      }
    : null)

  if (auth) {

    // Full profile built in-process by server/middleware/auth.ts for /app pages.
    // Seeding it here lets useUserProfile.ensure() short-circuit on SSR, so the
    // layout never makes an internal $fetch('/api/profile') round-trip.
    //
    // The status MUST be seeded alongside it. `event.context.profile` is only ever
    // set after buildUserProfile() resolves successfully (server/middleware/auth.ts),
    // so its presence here is exactly the 'ok' case — but leaving `user-profile-status`
    // at its default meant `ensure()`'s `if (profile.value) return` short-circuit left
    // the status stuck at 'idle' forever on an SSR-rendered page, and `gateFor`
    // (shared/onboarding/flow.ts) treats an unresolved status as "nothing to gate on." That let
    // an org-less user straight into /app on any cold load, refresh, or pasted link —
    // the one path where the profile arrives pre-seeded instead of freshly fetched.
    // See AlexAmin/marcus#243.
    const profile = event?.context?.profile
    if (profile) {
      useState('user-profile', () => profile)
      useState('user-profile-status', () => 'ok' as const)
    }
  }
})
